import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { FaDollarSign, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';
import { RootState } from '../../redux/Store';
import api from '../../utils/api';
import { buildSignBroadcast } from '../../utils/kleverTx';
import {
  SettingsPanel,
  SettingsRow,
  SettingsSaveButton,
  SettingsNote,
  SettingsSaveStatus,
} from './primitives';
import './FeesSettings.scss';

interface ProfileFees {
  dmFee: string;
  dmSpotlightFee: string;
  viewFee: string;
  liveSessionFee: string;
  consultationFee: string;
  subscriptionFee: string;
  subscriptionPeriodSeconds: number;
}

interface NFTProfile {
  id: string;
  nftProfileId: number;
  creator: {
    username: string;
    displayName: string;
  };
}

type FeeKey =
  | 'dmFee'
  | 'dmSpotlightFee'
  | 'viewFee'
  | 'subscriptionFee'
  | 'liveSessionFee'
  | 'consultationFee';

interface FeeRowConfig {
  key: FeeKey;
  title: string;
  description: string;
  placeholder: string;
}

const FEE_ROWS: FeeRowConfig[] = [
  { key: 'dmFee',            title: 'DM Cost',                 description: 'Cost for others to send you a direct message (0 = free)',           placeholder: '0' },
  { key: 'dmSpotlightFee',   title: 'DM Spotlight Fee',        description: 'Cost for others to spotlight their DM to you (stays until read)',  placeholder: '10' },
  { key: 'viewFee',          title: 'Profile Access (one-time)', description: 'One-time cost for permanent access to your profile (0 = public)', placeholder: '0' },
  { key: 'subscriptionFee',  title: 'Subscription',            description: 'Recurring subscription for exclusive content and features',         placeholder: '0' },
  { key: 'liveSessionFee',   title: 'Live Session Fee',        description: 'Cost to join your live group sessions (0 = free)',                  placeholder: '0' },
  { key: 'consultationFee',  title: 'Consultation Fee',        description: 'Cost for 1-on-1 consultation or call (0 = free)',                   placeholder: '0' },
];

export const FeesSettings: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth?.user);
  const isEmailUser = user?.authMethod === 'email';

  const [nftProfiles, setNftProfiles] = useState<NFTProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [fees, setFees] = useState<ProfileFees>({
    dmFee: '0',
    dmSpotlightFee: '10',
    viewFee: '0',
    liveSessionFee: '0',
    consultationFee: '0',
    subscriptionFee: '0',
    subscriptionPeriodSeconds: 2592000,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isFeesLoading, setIsFeesLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SettingsSaveStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadNFTProfiles();
  }, [user?.username]);

  useEffect(() => {
    if (selectedProfileId) {
      loadProfileFees(selectedProfileId);
    }
  }, [selectedProfileId]);

  const loadNFTProfiles = async () => {
    if (!user?.username) return;
    try {
      setIsLoading(true);
      const response = await api.get(`/user-profile/@${user.username}/nft-profiles`, {
        params: { as: 'creator' },
      });
      const createdProfiles: NFTProfile[] = response.data.profiles || [];
      setNftProfiles(createdProfiles);
      if (createdProfiles.length > 0) {
        setSelectedProfileId(createdProfiles[0].nftProfileId);
      }
    } catch (error: any) {
      console.error('Failed to load NFT profiles:', error);
      setErrorMessage('Failed to load your blockchain profiles');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProfileFees = async (profileId: number) => {
    try {
      setIsFeesLoading(true);
      const response = await api.get(`/blockchain-profiles/${profileId}/fees`);
      if (response.data.success && response.data.fees) {
        setFees(response.data.fees);
      }
    } catch (error: any) {
      console.error('Failed to load profile fees:', error);
      setErrorMessage('Failed to load current fees');
    } finally {
      setIsFeesLoading(false);
    }
  };

  // Clamp KLV amount inputs to 2 decimals. Browser `step="0.01"` is
  // only a hint — users can paste or type more digits — so we trim on
  // every change. Empty strings pass through so the user can clear
  // the field, and a lone trailing decimal point is preserved while
  // they're mid-type ("12." → user is about to enter the fractional).
  const clampDecimals = (value: string, max = 2): string => {
    if (value === '' || value === '.') return value;
    const dotIndex = value.indexOf('.');
    if (dotIndex === -1) return value;
    const whole = value.slice(0, dotIndex);
    const fractional = value.slice(dotIndex + 1, dotIndex + 1 + max);
    return `${whole}.${fractional}`;
  };

  const handleFeeChange = (field: keyof ProfileFees, value: string) => {
    if (field === 'subscriptionPeriodSeconds') {
      setFees({ ...fees, [field]: parseInt(value) || 0 });
    } else {
      setFees({ ...fees, [field]: clampDecimals(value) });
    }
  };

  const handleSave = async () => {
    if (!selectedProfileId) {
      setErrorMessage('No profile selected');
      return;
    }
    setIsSaving(true);
    setSaveStatus('saving');
    setErrorMessage('');
    try {
      const response = await api.post(`/blockchain-profiles/${selectedProfileId}/fees/execute`, {
        dmFee: fees.dmFee,
        dmSpotlightFee: fees.dmSpotlightFee,
        viewFee: fees.viewFee,
        liveSessionFee: fees.liveSessionFee,
        consultationFee: fees.consultationFee,
        subscriptionFee: fees.subscriptionFee,
        subscriptionPeriodSeconds: fees.subscriptionPeriodSeconds,
      });

      if (!isEmailUser) {
        const { payload, metadata, network, kdaFee } = response.data;
        const txHash = await buildSignBroadcast({ payload, metadata, network, kdaFee });
        await api.put(`/blockchain-profiles/${selectedProfileId}/fees`, {
          dmFee: fees.dmFee,
          dmSpotlightFee: fees.dmSpotlightFee,
          viewFee: fees.viewFee,
          liveSessionFee: fees.liveSessionFee,
          consultationFee: fees.consultationFee,
          subscriptionFee: fees.subscriptionFee,
          subscriptionPeriodSeconds: fees.subscriptionPeriodSeconds,
          txHash,
        });
      }

      setSaveStatus('saved');
      toast.success('Fees updated');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch (error: any) {
      console.error('Failed to update fees:', error);
      const resData = error.response?.data;
      const details = resData?.details ? ` — ${JSON.stringify(resData.details)}` : '';
      const msg = (resData?.error || error.message || 'Failed to update fees') + details;
      setErrorMessage(msg);
      toast.error(msg);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || isFeesLoading) {
    return (
      <SettingsPanel title="Fees Settings" description="Loading your fees from the blockchain…">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="fees-skeleton-row" />
        ))}
      </SettingsPanel>
    );
  }

  if (nftProfiles.length === 0) {
    return (
      <SettingsPanel title="Fees Settings" description="Set custom pricing for your profile interactions">
        <SettingsRow
          icon={<FaInfoCircle />}
          title="No blockchain profile found"
          description="You need to create a blockchain profile before you can set custom fees. Blockchain profiles allow you to monetize your content and interactions."
        />
      </SettingsPanel>
    );
  }

  return (
    <SettingsPanel
      title="Fees Settings"
      description="Set custom pricing for your profile interactions (all amounts in KLV)"
    >
      {errorMessage && (
        <SettingsNote tone="warning">
          <p>
            <FaExclamationTriangle /> {errorMessage}
          </p>
        </SettingsNote>
      )}

      {FEE_ROWS.map((row) => {
        const value = fees[row.key];
        const isSubscription = row.key === 'subscriptionFee';
        const showPeriod = isSubscription && parseFloat(fees.subscriptionFee) > 0;
        return (
          <SettingsRow
            key={row.key}
            icon={<FaDollarSign />}
            title={row.title}
            description={row.description}
            control={
              <div className="fees-settings-input-group">
                <input
                  type="number"
                  value={value}
                  onChange={(e) => handleFeeChange(row.key, e.target.value)}
                  placeholder={row.placeholder}
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  disabled={isSaving}
                  aria-label={row.title}
                />
                <span className="fees-settings-input-suffix">KLV</span>
              </div>
            }
          >
            {showPeriod && (
              <div className="fees-settings-subscription-period">
                <label htmlFor="subscription-period">Subscription period</label>
                <select
                  id="subscription-period"
                  value={fees.subscriptionPeriodSeconds}
                  onChange={(e) => handleFeeChange('subscriptionPeriodSeconds', e.target.value)}
                  disabled={isSaving}
                >
                  <option value="2592000">30 days (1 month)</option>
                  <option value="7776000">90 days (3 months)</option>
                  <option value="15552000">180 days (6 months)</option>
                  <option value="31536000">365 days (1 year)</option>
                </select>
              </div>
            )}
          </SettingsRow>
        );
      })}

      <SettingsSaveButton
        status={saveStatus}
        onClick={handleSave}
        disabled={isSaving || !selectedProfileId}
        idleLabel="Save changes"
      />

      <SettingsNote tone="info">
        <p>
          <strong>Note:</strong> Updating fees requires a blockchain transaction and wallet approval.
          The network fee to save this change is roughly <strong>7 KLV</strong>.
          All fees you set are paid in KLV. Setting a fee to 0 makes that feature free for everyone.
        </p>
      </SettingsNote>
    </SettingsPanel>
  );
};

export default FeesSettings;
